import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MapPin, Briefcase, Calendar, Shield, CreditCard, Building2, Bell, CheckCircle, XCircle, Pencil, Plus } from 'lucide-react';
import apiClient from '../api/client';
import LoadingSpinner from '../components/LoadingSpinner';
import { CustomerDetailDto, CustomerProductDetailDto } from '../types/dtos';
import FeedbackDialog, { FeedbackType } from '../components/FeedbackDialog';
import CustomerProductDetailsModal from '../components/CustomerProductDetailsModal';
import KYCRejectionModal from '../components/KYCRejectionModal';
import KYCEditModal from '../components/KYCEditModal';
import AssignProductModal from '../components/AssignProductModal';
import { resolveUrl } from '../utils/urlUtils';


export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [customer, setCustomer] = useState<CustomerDetailDto | null>(null);
  const [products, setProducts] = useState<CustomerProductDetailDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<CustomerProductDetailDto | null>(null);
  const [isRejectionModalOpen, setIsRejectionModalOpen] = useState(false);
  const [isKycEditModalOpen, setIsKycEditModalOpen] = useState(false);
  const [isAssignModalOpen, setIsAssignModalOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [feedback, setFeedback] = useState<{
    isOpen: boolean;
    type: FeedbackType;
    title: string;
    message: string;
    primaryActionLabel?: string;
    onPrimaryAction?: () => void;
    secondaryActionLabel?: string;
  }>({
    isOpen: false,
    type: 'success',
    title: '',
    message: '',
  });

  const triggerFeedback = (config: Omit<typeof feedback, 'isOpen'>) => {
    setFeedback({ ...config, isOpen: true });
  };

  const handleStatusUpdate = async (newStatus: string, reason?: string) => {
    const isRejecting = newStatus.toLowerCase() === 'rejected';

    // If rejecting and no reason provided, open the modal
    if (isRejecting && !reason) {
      setIsRejectionModalOpen(true);
      return;
    }

    triggerFeedback({
      type: 'confirm',
      title: isRejecting ? 'Reject KYC Submission' : 'Update KYC Status',
      message: isRejecting
        ? `Are you sure you want to reject this KYC submission? Feedback: "${reason}"`
        : `Are you sure you want to set the KYC status to ${newStatus}?`,
      primaryActionLabel: isRejecting ? 'Confirm Rejection' : `Yes, Set to ${newStatus}`,
      onPrimaryAction: async () => {
        try {
          await apiClient.put(`/admin/customers/${id}/kyc-status`, { status: newStatus, reason });
          setCustomer(prev => prev ? { ...prev, kycStatus: newStatus, kycRejectionReason: reason } : null);
          triggerFeedback({
            type: 'success',
            title: isRejecting ? 'Customer Rejected' : 'Status Updated',
            message: isRejecting ? 'The customer has been notified of the rejection.' : 'Customer KYC status has been updated successfully.',
          });
        } catch (error: any) {
          console.error("Failed to update status", error);
          triggerFeedback({
            type: 'error',
            title: 'Update Failed',
            message: error.response?.data?.message || 'An error occurred while updating the status. Please try again.',
          });
        }
      },
      secondaryActionLabel: 'Cancel',
    });
  };
  const handleKycSaved = (updated: Partial<CustomerDetailDto>) => {
    setCustomer(prev => prev ? { ...prev, ...updated } : null);
    triggerFeedback({
      type: 'success',
      title: 'KYC Updated',
      message: 'Customer KYC details have been saved successfully.',
    });
  };

  useEffect(() => {
    const fetchCustomer = async () => {
      try {
        const [customerRes, productsRes] = await Promise.all([
          apiClient.get<CustomerDetailDto>(`/admin/customers/${id}`),
          apiClient.get<CustomerProductDetailDto[]>(`/admin/customers/${id}/products`)
        ]);
        setCustomer(customerRes.data);
        setProducts(productsRes.data);
      } catch (err: any) {
        console.error("Failed to load customer details", err);
        setError(err.response?.data?.message || "Customer not found");
      } finally {
        setLoading(false);
      }
    };
    fetchCustomer();
  }, [id]);

  if (loading) return (
    <div className="w-full flex items-center justify-center min-h-[400px]">
      <LoadingSpinner />
    </div>
  );
  if (error) return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto p-6">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 text-red-700">
          <p className="font-bold">Error</p>
          <p>{error}</p>
          <button
            onClick={() => navigate('/admin/customers')}
            className="mt-4 text-sm font-medium underline"
          >
            Back to Customers
          </button>
        </div>
      </div>
    </div>
  );
  if (!customer) return <div>Customer not found</div>;

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto">

          <button onClick={() => navigate('/admin/customers')} className="flex items-center text-gray-500 hover:text-gray-700 mb-6">
            <ArrowLeft size={18} className="mr-1" /> Back to Customers
          </button>

          {/* Header Card */}
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6 border border-gray-200 flex flex-col md:flex-row items-start md:items-center gap-6">
            <img
              src={customer.profileImageBase64 || resolveUrl(customer.profileImageUrl) || "/images/default-avatar.png"}
              alt="Profile"
              className="w-24 h-24 rounded-full object-cover border-4 border-gray-100"
            />
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{customer.firstName} {customer.lastName}</h1>
              <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600">
                <span className="flex items-center gap-1"><Mail size={14} /> {customer.email}</span>
                <span className="flex items-center gap-1"><Phone size={14} /> {customer.phoneNumber}</span>
                <span className="flex items-center gap-1"><MapPin size={14} /> {customer.city}, {customer.country}</span>
              </div>
            </div>
            <div className="flex gap-4 text-center">
              <div className="bg-blue-50 px-4 py-2 rounded-lg">
                <div className="text-xl font-bold text-blue-900">{customer.productsCount}</div>
                <div className="text-xs text-blue-700 uppercase font-semibold">Products</div>
              </div>
              <div className="bg-purple-50 px-4 py-2 rounded-lg">
                <div className="text-xl font-bold text-purple-900">{customer.claimsCount}</div>
                <div className="text-xs text-purple-700 uppercase font-semibold">Claims</div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* Left Col: Personal & Address */}
            <div className="lg:col-span-2 space-y-6">

              {/* Identity */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Shield className="text-blue-900" size={20} /> Identity Information
                  </h3>
                  <button
                    onClick={() => setIsKycEditModalOpen(true)}
                    className="flex items-center gap-1.5 text-xs font-bold text-blue-700 hover:text-blue-900 border border-blue-200 hover:border-blue-400 px-3 py-1.5 rounded-lg transition-all"
                  >
                    <Pencil size={13} /> Edit KYC
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                  <div><label className="text-xs text-gray-500 uppercase">National ID</label><p className="font-medium">{customer.nationalId || "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">Passport</label><p className="font-medium">{customer.passport || "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">TPIN</label><p className="font-medium">{customer.tpin || "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">Date of Birth</label><p className="font-medium">{customer.dateOfBirth ? new Date(customer.dateOfBirth).toLocaleDateString() : "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">Gender</label><p className="font-medium">{customer.gender || "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">Marital Status</label><p className="font-medium">{customer.maritalStatus || "N/A"}</p></div>
                </div>
              </div>

              {/* Address & Employment */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="text-blue-900" size={20} /> Employment & Address
                </h3>
                <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                  <div className="col-span-2"><label className="text-xs text-gray-500 uppercase">Physical Address</label><p className="font-medium">{customer.physicalAddress || "N/A"}</p></div>
                  <div className="col-span-2"><label className="text-xs text-gray-500 uppercase">Postal Address</label><p className="font-medium">{customer.physicalAddress || "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">Occupation</label><p className="font-medium">{customer.occupation || "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">Nationality</label><p className="font-medium">{customer.nationality || "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">Insurance Plan</label><p className="font-medium capitalize">{customer.currentPlan || "N/A"}</p></div>
                  <div><label className="text-xs text-gray-500 uppercase">Timezone</label><p className="font-medium">{customer.timezone || "N/A"}</p></div>
                </div>
              </div>

              {/* Corporate Info (Conditional) */}
              {(customer.registrationNo || customer.organisationType) && (
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <Building2 className="text-blue-900" size={20} /> Corporate Information
                  </h3>
                  <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                    <div><label className="text-xs text-gray-500 uppercase">Org Type</label><p className="font-medium">{customer.organisationType || "N/A"}</p></div>
                    <div><label className="text-xs text-gray-500 uppercase">Reg No</label><p className="font-medium">{customer.registrationNo || "N/A"}</p></div>
                    <div><label className="text-xs text-gray-500 uppercase">Industry</label><p className="font-medium">{customer.industry || "N/A"}</p></div>
                    <div><label className="text-xs text-gray-500 uppercase">Contact Person</label><p className="font-medium">{customer.contactPerson || "N/A"}</p></div>
                    <div className="col-span-2"><label className="text-xs text-gray-500 uppercase">Business Location</label><p className="font-medium">{customer.businessLocation || "N/A"}</p></div>
                    {customer.dateOfIncorporation && (
                      <div><label className="text-xs text-gray-500 uppercase">Date of Inc.</label><p className="font-medium">{new Date(customer.dateOfIncorporation).toLocaleDateString()}</p></div>
                    )}
                  </div>
                </div>
              )}

              {/* Documents & Verification */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Shield className="text-blue-900" size={20} /> Documents & Verification
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <DocumentPreview
                    label="National ID"
                    url={customer.nationalIdImageUrl}
                    base64={customer.nationalIdImageBase64}
                    backupText={customer.nationalId}
                  />
                  <DocumentPreview
                    label="Passport / DL"
                    url={customer.passportImageUrl}
                    base64={customer.passportImageBase64}
                    backupText={customer.passport}
                  />
                  <DocumentPreview
                    label="Other ID"
                    url={customer.otherIdImageUrl}
                    base64={customer.otherIdImageBase64}
                  />
                  <DocumentPreview
                    label="Signature"
                    url={customer.signatureImageUrl}
                    base64={customer.signatureImageBase64}
                  />
                </div>
              </div>

              {/* Purchased Products */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                    <Briefcase className="text-blue-900" size={20} /> Purchased Products
                  </h3>
                  <button
                    onClick={() => setIsAssignModalOpen(true)}
                    className="flex items-center gap-1.5 text-xs font-bold text-teal-700 hover:text-teal-900 border border-teal-200 hover:border-teal-400 px-3 py-1.5 rounded-lg transition-all"
                  >
                    <Plus size={13} /> Assign Product
                  </button>
                </div>
                {products.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left">
                      <thead>
                        <tr className="border-b border-gray-100">
                          <th className="py-3 text-xs font-bold text-gray-400 uppercase">Product</th>
                          <th className="py-3 text-xs font-bold text-gray-400 uppercase">Status</th>
                          <th className="py-3 text-xs font-bold text-gray-400 uppercase">Premium</th>
                          <th className="py-3 text-xs font-bold text-gray-400 uppercase">Renewal/Expiry</th>
                          <th className="py-3 text-right"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {products.map((p) => (
                          <tr key={p.id} className="group hover:bg-gray-50/50 transition-colors">
                            <td className="py-3">
                              <div className="flex items-center gap-3">
                                {p.imageUrl && (
                                  <img src={resolveUrl(p.imageUrl) || ""} className="w-8 h-8 rounded-lg object-cover" alt="" />
                                )}
                                <div>
                                  <div className="font-bold text-gray-900 text-sm">{p.name}</div>
                                  <div className="text-[10px] text-gray-500 font-mono">{p.instanceId}</div>
                                </div>
                              </div>
                            </td>
                            <td className="py-3">
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${p.status === 'Active' ? 'bg-green-100 text-green-700' :
                                p.status === 'Pending' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                                }`}>
                                {p.status}
                              </span>
                            </td>
                            <td className="py-3">
                              <div className="text-sm font-bold text-blue-900">
                                MK {(p.monthlyPremium || p.onceOffPremium || 0).toLocaleString()}
                              </div>
                              <div className="text-[10px] text-gray-400">{p.premiumFrequency || 'Monthly'}</div>
                            </td>
                            <td className="py-3">
                              <div className="text-xs font-medium text-gray-700">
                                {p.premiumDueDate ? new Date(p.premiumDueDate).toLocaleDateString() : 'N/A'}
                              </div>
                              <div className="text-[10px] text-gray-400">
                                {p.expiryDate ? `Exp: ${new Date(p.expiryDate).toLocaleDateString()}` : 'No Expiry'}
                              </div>
                            </td>
                            <td className="py-3 text-right">
                              <button
                                onClick={() => setSelectedProduct(p)}
                                className="text-xs font-bold text-blue-600 hover:text-blue-800 underline"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 bg-gray-50 rounded-xl border border-dashed border-gray-200">
                    <Briefcase className="mx-auto text-gray-300 mb-2" size={32} />
                    <p className="text-sm text-gray-500">No products purchased yet.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Right Col: Status & Beneficiaries */}
            <div className="space-y-6">

              {/* KYC Status Card */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">KYC Status</h3>
                <div className="flex items-center justify-between mt-4">
                  <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${(customer.kycStatus === 'Approved' || customer.kycStatus === 'Verified') ? 'bg-green-100 text-green-800' :
                    customer.kycStatus === 'Rejected' ? 'bg-red-100 text-red-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                    {customer.kycStatus === 'Verified' ? 'Approved' : customer.kycStatus}
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Registered on: {new Date(customer.createdAt).toLocaleDateString()}
                </p>

                {customer.kycStatus === 'Rejected' && customer.kycRejectionReason && (
                  <div className="mt-4 p-3 bg-red-50 border border-red-100 rounded-lg">
                    <label className="text-[10px] text-red-600 uppercase font-black tracking-widest block mb-1">Rejection Reason</label>
                    <p className="text-xs text-red-700 leading-relaxed font-medium">{customer.kycRejectionReason}</p>
                  </div>
                )}

                <div className="mt-6 space-y-3">
                  {customer.kycStatus !== 'Approved' && customer.kycStatus !== 'Verified' && (
                    <button
                      onClick={() => handleStatusUpdate('Approved')}
                      className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                      <CheckCircle size={18} /> Approve KYC
                    </button>
                  )}
                  {customer.kycStatus !== 'Rejected' && (
                    <button
                      onClick={() => handleStatusUpdate('Rejected')}
                      className="w-full flex items-center justify-center gap-2 border border-red-200 text-red-600 hover:bg-red-50 py-2 px-4 rounded-lg font-medium transition-colors"
                    >
                      <XCircle size={18} /> Reject KYC
                    </button>
                  )}
                </div>
              </div>

              {/* Bank Details */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <CreditCard className="text-blue-900" size={20} /> Bank Details
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Bank Name</label>
                    <p className="font-medium">{customer.bankName || "N/A"}</p>
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 uppercase">Account Number</label>
                    <p className="font-medium">{customer.accountNo || "N/A"}</p>
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Bell className="text-blue-900" size={20} /> Notifications
                </h3>
                {customer.notifications ? (
                  <div className="space-y-3">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">Email Updates</span>
                      <span className={customer.notifications.email.updates ? "text-green-600 font-bold" : "text-gray-400"}>
                        {customer.notifications.email.updates ? "ON" : "OFF"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600">SMS Updates</span>
                      <span className={customer.notifications.sms.updates ? "text-green-600 font-bold" : "text-gray-400"}>
                        {customer.notifications.sms.updates ? "ON" : "OFF"}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm border-t pt-2">
                      <span className="text-gray-600">Marketing</span>
                      <span className={customer.notifications.email.marketing ? "text-purple-600 font-bold" : "text-gray-400"}>
                        {customer.notifications.email.marketing ? "Subscribed" : "Unsubscribed"}
                      </span>
                    </div>
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm italic">Default settings applied.</p>
                )}
              </div>

              {/* Beneficiaries */}
              <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Beneficiaries</h3>
                {customer.beneficiaries && customer.beneficiaries.length > 0 ? (
                  <div className="space-y-4">
                    {customer.beneficiaries.map((b, i) => (
                      <div key={i} className="p-3 bg-gray-50 rounded-lg border border-gray-100">
                        <div className="font-medium text-gray-900">{b.name}</div>
                        <div className="text-xs text-gray-500 flex justify-between mt-1">
                          <span>{b.relationship}</span>
                          <span className="font-bold text-blue-700">{b.percentage}%</span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm italic">No beneficiaries listed.</p>
                )}
              </div>

            </div>
          </div>
      </div>

      <FeedbackDialog
        isOpen={feedback.isOpen}
        onClose={() => setFeedback(prev => ({ ...prev, isOpen: false }))}
        type={feedback.type}
        title={feedback.title}
        message={feedback.message}
        primaryActionLabel={feedback.primaryActionLabel}
        onPrimaryAction={feedback.onPrimaryAction}
        secondaryActionLabel={feedback.secondaryActionLabel}
      />

      {selectedProduct && (
        <CustomerProductDetailsModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
      {isRejectionModalOpen && (
        <KYCRejectionModal
          isOpen={isRejectionModalOpen}
          onClose={() => setIsRejectionModalOpen(false)}
          onConfirm={(reason) => handleStatusUpdate('Rejected', reason)}
          customerName={`${customer.firstName} ${customer.lastName}`}
        />
      )}

      {isKycEditModalOpen && (
        <KYCEditModal
          isOpen={isKycEditModalOpen}
          onClose={() => setIsKycEditModalOpen(false)}
          customer={customer}
          onSaved={handleKycSaved}
        />
      )}

      {customer && (
        <AssignProductModal
          isOpen={isAssignModalOpen}
          onClose={() => setIsAssignModalOpen(false)}
          onSuccess={() => {
            // Refresh products
            apiClient.get<CustomerProductDetailDto[]>(`/admin/customers/${id}/products`)
              .then(res => setProducts(res.data));

            triggerFeedback({
              type: 'success',
              title: 'Product Assigned',
              message: 'The product has been successfully assigned to the customer.',
            });
          }}
          customerId={customer.id}
          customerName={`${customer.firstName} ${customer.lastName}`}
        />
      )}
    </div>
  );
}


// Helper Component for Document Preview
function DocumentPreview({ label, url, base64, backupText }: { label: string; url?: string; base64?: string; backupText?: string }) {
  const fullUrl = base64 || resolveUrl(url);


  return (
    <div className="flex flex-col gap-2 shadow-sm rounded-xl border border-gray-100 p-3 bg-gray-50/30">
      <label className="text-[10px] text-gray-500 uppercase font-black tracking-widest">{label}</label>
      <div className="relative group">
        <div className="aspect-[4/3] rounded-lg border border-gray-200 bg-white overflow-hidden flex items-center justify-center">
          {fullUrl ? (
            <img
              src={fullUrl}
              alt={label}
              className="w-full h-full object-contain cursor-pointer hover:scale-105 transition-transform"
              onClick={() => window.open(fullUrl, '_blank')}
            />
          ) : (
            <div className="text-center p-4">
              <Shield size={24} className="mx-auto text-gray-300 mb-2" />
              <p className="text-[10px] text-gray-400 font-medium">No document uploaded</p>
            </div>
          )}
        </div>
        {backupText && !fullUrl && (
          <p className="mt-1 text-[10px] font-bold text-gray-700 truncate text-center">{backupText}</p>
        )}
        {fullUrl && (
          <div className="absolute top-2 right-2 flex gap-1">
            <button
              onClick={() => window.open(fullUrl, '_blank')}
              className="bg-white/90 backdrop-blur-sm p-1.5 rounded-lg shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
            >
              <Shield size={14} className="text-blue-900" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}